import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const dropdownTimeoutRef = useRef(null)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false)
    setActiveDropdown(null)
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
  }, [location])

  const handleDropdownEnter = (idx) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
      dropdownTimeoutRef.current = null
    }
    setActiveDropdown(idx)
  }

  const handleDropdownLeave = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null)
    }, 200)
  }

  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current)
      }
    }
  }, [])

  const isActive = (path) => {
    if (path === '/' && location.pathname !== '/') return false
    return location.pathname.startsWith(path)
  }

  const menuItems = [
    { label: 'Forside', href: '/' },
    { label: 'Om mig', href: '/om-mig' },
    { label: 'Psykoterapi', href: '/psykoterapi' },
    {
      label: 'Ydelser',
      href: '/ydelser',
      children: [
        { label: 'Alle Ydelser', href: '/ydelser' },
        { label: 'Integrativ Psykoterapeut', href: '/ydelser/integrativ-psykoterapeut' },
        { label: 'Familiebehandler', href: '/ydelser/familiebehandler' },
        { label: 'Samværskonsulent', href: '/ydelser/samvaerskonsulent' },
        { label: 'Supervisor', href: '/ydelser/supervisor' }
      ]
    },
    { label: 'Priser & praktisk', href: '/priser' },
    { label: 'Kontakt', href: '/kontakt' }
  ]

  return (
    <header className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
      isOpen 
        ? 'bg-brand-offwhite border-b border-brand-sand/20 py-4' 
        : isScrolled 
          ? 'bg-brand-offwhite/95 backdrop-blur-md shadow-sm py-3.5 border-b border-brand-sand/20' 
          : 'bg-brand-offwhite/90 backdrop-blur-md border-b border-brand-sand/30 py-4 sm:py-5'
    }`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo / Brand (Left) */}
        <Link to="/" className="flex items-center gap-3 group shrink-0" aria-label="Terapien.dk - Forside">
          <div className="flex flex-col pb-1.5 sm:pb-2">
            <span className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-brand-dark group-hover:text-brand-sage transition-colors flex items-center gap-1">
              Terapien<span className="text-brand-sage">.dk</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-sage ml-0.5" />
            </span>
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-brand-gray/60 font-semibold -mt-1">
              v/ Birgitte Vilsgaard
            </span>
          </div>
        </Link>

        {/* Desktop Navigation (Center/Right) */}
        <nav className="hidden min-[1200px]:flex items-center gap-6 xl:gap-8">
          {menuItems.map((item, idx) => (
            <div
              key={idx}
              className="relative"
              onMouseEnter={() => item.children && handleDropdownEnter(idx)}
              onMouseLeave={() => item.children && handleDropdownLeave()}
            >
              {item.children ? (
                <div className="flex items-center">
                  <Link
                    to={item.href}
                    onClick={() => setActiveDropdown(null)}
                    className={`flex items-center gap-1.5 text-sm font-medium tracking-wide transition-colors duration-200 py-2 relative ${
                      isActive(item.href) ? 'text-brand-dark font-semibold' : 'text-brand-gray/80 hover:text-brand-dark'
                    }`}
                  >
                    {item.label}
                    <i className={`fa-light fa-chevron-down text-xs transition-transform duration-200 ${activeDropdown === idx ? 'rotate-180' : ''}`} />
                    {isActive(item.href) && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-sage rounded-full" />
                    )}
                  </Link>
                </div>
              ) : (
                <Link
                  to={item.href}
                  className={`text-sm font-medium tracking-wide transition-colors duration-200 py-2 relative ${
                    isActive(item.href) ? 'text-brand-dark font-semibold' : 'text-brand-gray/80 hover:text-brand-dark'
                  }`}
                >
                  {item.label}
                  {isActive(item.href) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-sage rounded-full" />
                  )}
                </Link>
              )}

              {/* Dropdown Menu */}
              {item.children && activeDropdown === idx && (
                <div
                  className="absolute top-full left-0 pt-2 w-64 z-50 animate-fadeIn"
                  onMouseEnter={() => handleDropdownEnter(idx)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <div className="bg-brand-offwhite border border-brand-sand/50 rounded-xl shadow-xl py-2 overflow-hidden">
                    {item.children.map((child, cIdx) => (
                      <Link
                        key={cIdx}
                        to={child.href}
                        onClick={() => setActiveDropdown(null)}
                        className={`block px-5 py-2.5 text-sm transition-colors duration-150 ${
                          location.pathname === child.href
                            ? 'bg-brand-sand/40 text-brand-dark font-semibold'
                            : 'text-brand-gray/80 hover:bg-brand-sand/30 hover:text-brand-dark'
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Right Desktop CTA & Mobile Hamburger */}
        <div className="flex items-center gap-4">
          {/* Desktop CTA Button */}
          <Link
            to="/kontakt"
            className="hidden min-[1200px]:inline-block bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-xs font-semibold uppercase tracking-wider px-5 py-3 rounded-full transition-all duration-200 hover:shadow-md active:scale-95"
          >
            Book en samtale
          </Link>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="min-[1200px]:hidden text-brand-dark hover:text-brand-sage p-2 transition-colors duration-200"
            aria-label="Toggle menu"
          >
            <i className={`fa-regular ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu Panel (Visible below 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden fixed inset-0 h-screen bg-brand-offwhite z-40 flex flex-col px-6 pt-24 pb-12 overflow-y-auto animate-slideDown">
          <div className="flex flex-col gap-6">
            {menuItems.map((item, idx) => (
              <div key={idx} className="border-b border-brand-sand/30 pb-4">
                {item.children ? (
                  <div>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                      className="flex items-center justify-between w-full text-lg font-serif font-semibold text-brand-dark"
                    >
                      {item.label}
                      <i className={`fa-light fa-chevron-down text-sm transition-transform duration-200 ${activeDropdown === idx ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === idx && (
                      <div className="flex flex-col gap-3 mt-3 pl-4 border-l border-brand-sage/30">
                        {item.children.map((child, cIdx) => (
                          <Link
                            key={cIdx}
                            to={child.href}
                            className="text-sm text-brand-gray/70 hover:text-brand-dark py-1"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`text-lg font-serif font-semibold block ${
                      isActive(item.href) ? 'text-brand-sage' : 'text-brand-dark'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="mt-auto pt-8 flex flex-col gap-4">
            <Link
              to="/kontakt"
              className="bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-center text-sm font-semibold uppercase tracking-wider py-4 rounded-full transition-all duration-200"
            >
              Book en samtale
            </Link>
            <div className="text-center text-xs text-brand-gray/50">
              <p>Ring i dag: <a href="tel:28839488" className="hover:text-brand-dark font-medium">28 83 94 88</a></p>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
